import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/auth-context';
import { API_URL } from '../config/api';
import { toast } from '../components/ui/use-toast';
import StripePricingTable from '../components/stripe/StripePricingTable';
import { 
  CreditCard,
  BarChart3,
  MessageSquare,
  FileText,
  AlertCircle,
  CheckCircle2,
  TrendingUp,
  Calendar,
  ExternalLink
} from 'lucide-react';

interface SubscriptionInfo {
  status: string;
  currentTier: string;
  trialEndsAt?: string;
  currentPeriodEndsAt?: string;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

interface UsageStats {
  conversationsToday: number;
  conversationsLimit: number;
  pagesIndexed: number;
  pagesLimit: number;
  date: string;
}

const LicensingEnhanced: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [loadingPortal, setLoadingPortal] = useState(false);

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchUsageStats();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/subscriptions/current`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubscription(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const response = await fetch(`${API_URL}/usage/daily-stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
    }
  };

  const openCustomerPortal = async () => {
    setLoadingPortal(true);
    try {
      const response = await fetch(`${API_URL}/subscriptions/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      
      if (response.ok) {
        const { url } = await response.json();
        window.open(url, '_blank');
      } else {
        throw new Error('Failed to create portal session');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to open billing portal. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoadingPortal(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'bg-gray-100 text-gray-800';
      case 'starter': return 'bg-blue-100 text-blue-800';
      case 'growth': return 'bg-purple-100 text-purple-800';
      case 'pro': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-7xl">
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Licensing & Billing</h1>
          <p className="text-muted-foreground mt-2">
            Manage your subscription and monitor your usage
          </p>
        </div>

        {/* Current Plan Overview */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Current Plan
                <Badge className={getTierColor(subscription?.currentTier || 'free')}>
                  {subscription?.currentTier?.toUpperCase() || 'FREE'}
                </Badge>
              </CardTitle>
              <CardDescription>Your active subscription details</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Status</span>
                  <div className="flex items-center gap-2">
                    {subscription?.status === 'active' ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span className="font-medium">Active</span>
                      </>
                    ) : subscription?.status === 'trialing' ? (
                      <>
                        <AlertCircle className="h-4 w-4 text-blue-600" />
                        <span className="font-medium">Trial</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="h-4 w-4 text-gray-600" />
                        <span className="font-medium">Free Plan</span>
                      </>
                    )}
                  </div>
                </div>
                
                {subscription?.currentPeriodEndsAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {subscription.status === 'trialing' ? 'Trial ends' : 'Renews on'}
                    </span>
                    <span className="font-medium">
                      {new Date(subscription.currentPeriodEndsAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                
                <div className="pt-4">
                  <Button 
                    onClick={openCustomerPortal}
                    disabled={loadingPortal || !subscription?.stripeCustomerId}
                    className="w-full"
                    variant="outline"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {loadingPortal ? 'Opening...' : 'Manage Billing'}
                    <ExternalLink className="h-3 w-3 ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Usage Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Today's Usage
              </CardTitle>
              <CardDescription>
                {usage?.date ? new Date(usage.date).toLocaleDateString() : 'Today'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Conversations */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Conversations</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {usage?.conversationsToday || 0} / {usage?.conversationsLimit || 10}
                    </span>
                  </div>
                  <Progress 
                    value={getUsagePercentage(usage?.conversationsToday || 0, usage?.conversationsLimit || 10)} 
                    className="h-2"
                  />
                </div>
                
                {/* Pages */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Pages Indexed</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {usage?.pagesIndexed || 0} / {usage?.pagesLimit || 10}
                    </span>
                  </div>
                  <Progress 
                    value={getUsagePercentage(usage?.pagesIndexed || 0, usage?.pagesLimit || 10)} 
                    className="h-2"
                  />
                </div>
                
                {/* Usage Warning */}
                {usage && usage.conversationsToday >= usage.conversationsLimit * 0.8 && (
                  <div className="bg-amber-50 border border-amber-200 p-3 rounded-md">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <p className="text-sm text-amber-800">
                        You're approaching your daily conversation limit
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Options */}
        <Tabs defaultValue="upgrade" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upgrade">Upgrade Plan</TabsTrigger>
            <TabsTrigger value="usage">Usage History</TabsTrigger>
          </TabsList>
          
          <TabsContent value="upgrade" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Choose Your Plan</CardTitle>
                <CardDescription>
                  Upgrade to unlock more conversations, pages, and features
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Stripe Pricing Table */}
                <div className="w-full">
                  <StripePricingTable />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="usage" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Usage History</CardTitle>
                <CardDescription>
                  Track your usage patterns over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-12 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>Usage history coming soon</p>
                  <p className="text-sm mt-2">
                    We're working on detailed analytics to help you understand your usage patterns
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LicensingEnhanced;